import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import {
  Token,
  TokenAmount,
  Trade,
  TradeType,
  Route,
  Pair,
  Percent,
} from "@uniswap/sdk";

import UNISWAP_ROUTER_ABI from "./abis/IUniswapV2Router02.json";
import ERC20_ABI from "./abis/ERC20.json";
import IUniswapV2FactoryABI from "./abis/IUniswapV2Factory.json";

const chainId = 167012;
const provider = new ethers.JsonRpcProvider("https://rpc.kasplextest.xyz", {
  name: "Kasplex Test",
  chainId,
}, {
    batchMaxCount: 1,
    batchMaxSize: 1,
    batchStallTime: 0,
  });

const TOKEN_A = new Token(
  chainId as any,
  "0x654A3287c317D4Fc6e8482FeF523Dc4572b563AA",
  18,
  "WKAS",
  "Wrapped KAS"
);
const TOKEN_B = new Token(
  chainId as any,
  "0xD33d07ccfEd8038cEd1aa393FbEf7D7dA72be20e",
  6,
  "USDT",
  "Tether"
);

const ROUTER_ADDRESS = "0x5A410f79f58a11344E3523d99820Cf231bc888bd";
const FACTORY_ADDRESS = "0xYourFactoryAddressHere"; // replace with your factory

const UNISWAP_FACTORY_ABI = [
  "function getPair(address tokenA, address tokenB) external view returns (address pair)",
];

const tokens = [TOKEN_A, TOKEN_B];

// Add type for window.ethereum
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare global {
  interface Window {
    ethereum?: any;
  }
}

const browserProvider = typeof window !== 'undefined' && window.ethereum ? new ethers.BrowserProvider(window.ethereum) : undefined;

// Utility to trim a string number to the correct number of decimals
function trimToDecimals(value: string, decimals: number) {
  if (!value.includes('.')) return value;
  const [int, dec] = value.split('.');
  return dec.length > decimals ? `${int}.${dec.slice(0, decimals)}` : value;
}

const Swap = () => {
  const [direction, setDirection] = useState<0 | 1>(0); // 0: A->B, 1: B->A
  const [inputAmount, setInputAmount] = useState("");
  const [estimatedOutput, setEstimatedOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState<string | null>(null);

  const router = new ethers.Contract(ROUTER_ADDRESS, UNISWAP_ROUTER_ABI, provider);

  // Connect wallet (Metamask)
  useEffect(() => {
    if (window.ethereum && account == null) {
      window.ethereum.request({ method: "eth_accounts" }).then((accounts: string[]) => {
        if (accounts.length > 0) setAccount(accounts[0]);
      });
    }
  }, [account]);

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
        setAccount(accounts[0]);
      } catch (e) {
        alert("Wallet connection rejected.");
      }
    } else {
      alert("No Ethereum wallet found. Please install MetaMask.");
    }
  };

  // Estimate output
  useEffect(() => {
    const estimate = async () => {
      if (!inputAmount || isNaN(Number(inputAmount))) {
        setEstimatedOutput("");
        return;
      }
      try {
        const path = direction === 0 ? [TOKEN_A.address, TOKEN_B.address] : [TOKEN_B.address, TOKEN_A.address];
        const amountIn = ethers.parseUnits(inputAmount, direction === 0 ? TOKEN_A.decimals : TOKEN_B.decimals);
        const amounts = await router.getAmountsOut(amountIn, path);
        const output = ethers.formatUnits(
          amounts[1],
          direction === 0 ? TOKEN_B.decimals : TOKEN_A.decimals
        );
        setEstimatedOutput(output);
      } catch (e) {
        setEstimatedOutput("");
      }
    };
    estimate();
  }, [inputAmount, direction]);

  // Approve token if needed
  const approveIfNeeded = async (tokenAddress: string, amount: bigint) => {
    if (!account || !browserProvider) return;
    const signer = await browserProvider.getSigner();
    const token = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
    const allowance = await token.allowance(account, ROUTER_ADDRESS);
    if (allowance < amount) {
      const tx = await token.approve(ROUTER_ADDRESS, amount);
      await tx.wait();
    }
  };

  // Perform swap
  const handleSwap = async () => {
    if (!account || !inputAmount || isNaN(Number(inputAmount)) || !browserProvider) return;
    setLoading(true);
    try {
      const signer = await browserProvider.getSigner();
      const routerWithSigner = router.connect(signer) as ethers.Contract;
      const path = direction === 0 ? [TOKEN_A.address, TOKEN_B.address] : [TOKEN_B.address, TOKEN_A.address];
      const amountIn = ethers.parseUnits(inputAmount, direction === 0 ? TOKEN_A.decimals : TOKEN_B.decimals);
      const outDecimals = direction === 0 ? TOKEN_B.decimals : TOKEN_A.decimals;
      const minAmountOutStr = trimToDecimals((Number(estimatedOutput) * 0.995).toString(), outDecimals);
      const minAmountOut = ethers.parseUnits(minAmountOutStr, outDecimals); // 0.5% slippage
      await approveIfNeeded(path[0], amountIn);
      const deadline = Math.floor(Date.now() / 1000) + 60 * 10; // 10 minutes
      const tx = await routerWithSigner.swapExactTokensForTokens(
        amountIn,
        minAmountOut,
        path,
        account,
        deadline
      );
      await tx.wait();
      alert("Swap successful!");
    } catch (e: any) {
      alert("Swap failed: " + (e?.message || e));
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 400, margin: "40px auto", padding: 24, border: "1px solid #eee", borderRadius: 12 }}>
      <h2>Kasplex Swap</h2>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        {account ? (
          <span style={{ fontSize: 12, color: '#555' }}>Connected: {account.slice(0, 6)}...{account.slice(-4)}</span>
        ) : (
          <button onClick={connectWallet}>Connect Wallet</button>
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setDirection(0)} disabled={direction === 0}>
          {TOKEN_A.symbol} → {TOKEN_B.symbol}
        </button>
        <button onClick={() => setDirection(1)} disabled={direction === 1} style={{ marginLeft: 8 }}>
          {TOKEN_B.symbol} → {TOKEN_A.symbol}
        </button>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label>
          From ({direction === 0 ? TOKEN_A.symbol : TOKEN_B.symbol}):
          <input
            type="number"
            min="0"
            value={inputAmount}
            onChange={e => setInputAmount(e.target.value)}
            style={{ marginLeft: 8, width: 120 }}
            disabled={loading || !account}
          />
        </label>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label>
          To ({direction === 0 ? TOKEN_B.symbol : TOKEN_A.symbol}):
          <input
            type="text"
            value={estimatedOutput}
            readOnly
            style={{ marginLeft: 8, width: 120, background: "#f9f9f9" }}
          />
        </label>
      </div>
      <button onClick={handleSwap} disabled={loading || !inputAmount || !estimatedOutput || !account}>
        {account ? (loading ? "Swapping..." : "Swap") : "Connect Wallet"}
      </button>
    </div>
  );
};

export default Swap;